import {
  CustomProductOptionConfigType,
  ICustomProductOptionConfig,
} from "@evennt-tech/api-v2-core";
import { IProductCustomConfigsSummary } from "@evennt-tech/api-v2-external";

/**
 * The product lists inline a product's enabled CUSTOM configs only while it
 * has few of them; a bigger matrix arrives with its `customConfigsSummary`
 * alone and its rows are loaded per product when a view needs them.
 */
export const hasLazyCustomConfigs = (
  summary: IProductCustomConfigsSummary | undefined,
  configs: Pick<ICustomProductOptionConfig, "type" | "enabled">[] = []
): boolean =>
  !!summary &&
  summary.count >
    configs.filter(
      ({ type, enabled }) =>
        type === CustomProductOptionConfigType.CUSTOM && enabled
    ).length;

/**
 * A product sold through its CUSTOM configs (DEFAULT disabled) whose rows the
 * list left out: views that let the user pick an option load them first.
 */
export const isAwaitingCustomConfigs = ({
  configs,
  customConfigsSummary,
}: {
  configs: Pick<ICustomProductOptionConfig, "type" | "enabled">[];
  customConfigsSummary?: IProductCustomConfigsSummary;
}): boolean => {
  const defaultConfig = configs.find(
    ({ type }) => type === CustomProductOptionConfigType.DEFAULT
  );

  return (
    !!defaultConfig &&
    !defaultConfig.enabled &&
    hasLazyCustomConfigs(customConfigsSummary, configs)
  );
};

type QtyOverrideContribution = Pick<
  IProductCustomConfigsSummary,
  "overrideCount" | "overrideQuantity" | "overrideQuantityLeft"
>;

const getQtyOverrideContribution = (
  quantity: number | null | undefined,
  purchasedQuantity: number
): QtyOverrideContribution =>
  typeof quantity === "number"
    ? {
        overrideCount: 1,
        overrideQuantity: quantity,
        overrideQuantityLeft: quantity
          ? Math.max(0, quantity - purchasedQuantity)
          : 0,
      }
    : { overrideCount: 0, overrideQuantity: 0, overrideQuantityLeft: 0 };

/**
 * The summary's override totals once the quantity overrides edited since it
 * was fetched are applied. `rows` are configs as loaded (`showQuantity` = the
 * override then); `getEditedQty` gives an edited one (`null` = removed,
 * `undefined` = not edited).
 */
export const applyQtyOverrideEdits = (
  summary: IProductCustomConfigsSummary,
  rows: {
    configId: string;
    showQuantity?: number;
    purchasedQuantity?: number;
  }[],
  getEditedQty: (configId: string) => number | null | undefined
): IProductCustomConfigsSummary =>
  rows.reduce((acc, { configId, showQuantity, purchasedQuantity = 0 }) => {
    const editedQty = getEditedQty(configId);
    if (editedQty === undefined) {
      return acc;
    }

    const before = getQtyOverrideContribution(showQuantity, purchasedQuantity);
    const after = getQtyOverrideContribution(editedQty, purchasedQuantity);

    return {
      ...acc,
      overrideCount:
        acc.overrideCount + after.overrideCount - before.overrideCount,
      overrideQuantity:
        acc.overrideQuantity + after.overrideQuantity - before.overrideQuantity,
      overrideQuantityLeft:
        acc.overrideQuantityLeft +
        after.overrideQuantityLeft -
        before.overrideQuantityLeft,
    };
  }, summary);
